import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { Segmenty } from '@/components/Segmenty';
import { PasSekcji } from '@/components/PasSekcji';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { formatCount, formatGrosze } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { bezWartosci, porzadek } from '@/lib/porzadekRankingu';
import { useAuth } from '@/lib/auth/AuthContext';
import { useDeliveryBySchool, useDeliveryHistory, useDishSales, useSchoolSales } from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';
import { joinKey } from '@/lib/crossSystem';
import type { SchoolSalesRow } from '@/lib/api/analytics';
import type { DeliverySchoolRow } from '@/lib/api/drive';
import { klasyfikacjaDrive, swiezoscDrive } from '@/lib/pochodzenieDanych';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SZKOŁY OD STRONY OFERTY — migracja do języka „SYGNAŁ" (kierunek C, R5-15-009)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * PYTANIE EKRANU: do których szkół trafia oferta restauracji, ile tam sprzedaje, jak tam dowozimy
 * i gdzie konkretne danie wypada nietypowo słabo na tle pozostałych szkół tej samej restauracji.
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH: wyłącznie realne API przez istniejące haki.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ PUŁAPKA ŹRÓDŁA: PARA SZKOŁA–RESTAURACJA POWTARZA SIĘ.                                     │
 * │ Grafik dostaw (`crm_delivery_schedule`, snapshot MSSQL `SchoolRestaurant`) ma ziarno       │
 * │ (szkoła, restauracja, DZIEŃ TYGODNIA) i własny `order_cutoff` w każdym wierszu — ta sama   │
 * │ para bywa tam pięć razy z pięcioma różnymi godzinami zamknięcia zamówień. Historia dostaw  │
 * │ jest jeszcze gęstsza: jeden wiersz na każdą pojedynczą dostawę.                            │
 * │                                                                                            │
 * │ Dlatego KAŻDA liczba „ile szkół" i „ile restauracji" na tym ekranie liczy zbiór RÓŻNYCH    │
 * │ identyfikatorów, nigdy wierszy. Zliczanie wierszy zawyżyłoby próbę — a próba wchodzi do    │
 * │ dowodu pod liczbą i do progów istotności, więc zawyżona próba nie jest kosmetyką: zamienia │
 * │ szum w „sygnał", który ktoś potem obdzwoni.                                                │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 */

/**
 * Wiersz sprzedaży szkoły z polem, którego KONTRAKT KLIENTA NIE DEKLARUJE.
 *
 * Backend (`crm/backend/routes/analytics.js`) liczy dla każdej szkoły
 * `restaurant_count = COUNT(DISTINCT ds.restaurant_tuszama_id)` po `crm_delivery_schedule`
 * (`is_active = TRUE`) i zwraca je w wierszu — a `lib/api/analytics.ts` tego pola nie ma,
 * więc było po cichu wyrzucane. Ta sama klasa defektu co `charts.daily_trend` w R5-15-006:
 * dane są liczone i tracone na granicy typów.
 *
 * Zawężenie trzymamy LOKALNIE i opcjonalnie: pole jest odczytywane, ale jego brak (starszy
 * backend) daje „brak danych", a nie zero. Wspólnego kontraktu nie ruszamy z poziomu ekranu.
 */
type WierszSzkoly = SchoolSalesRow & { restaurant_count?: number };

/** Okno pisane dosłownie, ze strzałką — stan pusty musi podać PRZEDZIAŁ, którego brak dotyczy. */
function oknoDoslownie(from: string, to: string): string {
  return `${from} → ${to}`;
}

export function RestaurantSchoolsPage() {
  const auth = useAuth();
  const [range] = useDateRange();
  const sales = useSchoolSales();
  const dishes = useDishSales();
  const delivery = useDeliveryBySchool();
  // Surowe dostawy: JEDYNE miejsce w API, w którym para restauracja–szkoła występuje wprost.
  // Limit 500 to maksimum, które backend Drive przyjmuje (clampInt 1..500) — ucięcie deklarujemy.
  const historia = useDeliveryHistory({ limit: 500 });
  const driveConnected = czyWidziDostawy(auth);

  const okno = oknoDoslownie(
    sales.data?.range.date_from ?? range.date_from,
    sales.data?.range.date_to ?? range.date_to,
  );

  // Stabilna referencja: `?? []` tworzyłoby nową tablicę przy każdym renderze i przeliczało
  // niżej wszystkie useMemo, w tym zliczanie unikalnych szkół.
  const wiersze = useMemo<readonly WierszSzkoly[]>(
    () => (sales.data?.schools ?? []) as readonly WierszSzkoly[],
    [sales.data],
  );

  /*
   * SZKOŁY UNIKALNE, NIE WIERSZE — patrz ramka w nagłówku pliku.
   *
   * Backend akurat tej listy broni się sam (kanon Szybkiego Alertu używa EXISTS zamiast JOIN-a
   * właśnie po to, żeby szkoła nie powieliła się przez grafik). Ale liczba, którą pokazujemy jako
   * bohatera ekranu i wpisujemy do dowodu jako PRÓBĘ, nie może zależeć od tego, że ktoś po drugiej
   * stronie pamiętał o DISTINCT. Liczymy zbiór różnych identyfikatorów, a rozjazd — gdyby kiedyś
   * wystąpił — pokazujemy zamiast go milcząco wygładzić.
   */
  const { szkolyUnikalne, wierszyBezIdentyfikatora, wierszyPowtorzonych } = useMemo(() => {
    const identyfikatory = new Set<number>();
    let bezId = 0;
    for (const r of wiersze) {
      const k = joinKey(r.school_id);
      if (k === null) {
        bezId += 1;
        continue;
      }
      identyfikatory.add(k);
    }
    return {
      szkolyUnikalne: identyfikatory.size,
      wierszyBezIdentyfikatora: bezId,
      wierszyPowtorzonych: wiersze.length - bezId - identyfikatory.size,
    };
  }, [wiersze]);

  /*
   * DOSTAWY PO IDENTYFIKATORZE SZKOŁY — z jawną obsługą kolizji.
   *
   * Poprzednia wersja budowała `new Map(rows.map(...))`. Gdy dwa rekordy szkoły po stronie Drive
   * wskazują ten sam `tuszama_id`, taka mapa po cichu zostawia OSTATNI i gubi dostawy z pozostałych.
   * Wynik wygląda wtedy jak kompletna punktualność szkoły, a jest punktualnością jednego z dwóch
   * rekordów, wybranego przypadkiem — czyli liczbą, której nie da się obronić.
   * Grupujemy, a przy kolizji pokazujemy brak zamiast losowo wybranej połowy prawdy.
   */
  const dostawyWgSzkoly = useMemo(() => {
    const grupy = new Map<number, DeliverySchoolRow[]>();
    for (const d of delivery.data?.schools ?? []) {
      const k = joinKey(d.tuszama_id);
      if (k === null) continue;
      const lista = grupy.get(k);
      if (lista) lista.push(d);
      else grupy.set(k, [d]);
    }
    return grupy;
  }, [delivery.data]);

  const szkolyZDostawami = dostawyWgSzkoly.size;
  const identyfikatoryNiejednoznaczne = useMemo(
    () => [...dostawyWgSzkoly.values()].filter((g) => g.length > 1).length,
    [dostawyWgSzkoly],
  );

  const outliersBySchool = useMemo(() => {
    const map = new Map<number, { meal_name: string | null; share_ratio: number }[]>();
    for (const o of dishes.data?.school_outliers ?? []) {
      const k = joinKey(o.school_id);
      if (k === null) continue;
      const lista = map.get(k);
      if (lista) lista.push({ meal_name: o.meal_name, share_ratio: o.share_ratio });
      else map.set(k, [{ meal_name: o.meal_name, share_ratio: o.share_ratio }]);
    }
    return map;
  }, [dishes.data]);

  /*
   * ZASIĘG DOWOZU — która restauracja dowozi do których szkół.
   *
   * Historia dostaw ma jeden wiersz na DOSTAWĘ, więc para restauracja–szkoła powtarza się tyle
   * razy, ile razy w oknie coś przyjechało. Liczba szkół restauracji = rozmiar zbioru RÓŻNYCH
   * identyfikatorów szkół; liczba wierszy stoi obok jako osobna kolumna, żeby było widać, o ile
   * różni się jedno od drugiego. Rekordy bez identyfikatora wypadają ze zbioru zamiast trafić
   * do niego przypadkiem — i są policzone osobno.
   */
  const zasieg = useMemo(() => {
    const wpisy = new Map<
      number,
      { nazwa: string | null; szkoly: Map<number, string | null>; dostaw: number; bezIdSzkoly: number }
    >();
    let dostawBezIdRestauracji = 0;
    for (const d of historia.data?.history ?? []) {
      const rid = joinKey(d.restaurant_tuszama_id);
      if (rid === null) {
        dostawBezIdRestauracji += 1;
        continue;
      }
      const wpis = wpisy.get(rid) ?? { nazwa: d.restaurant_name, szkoly: new Map(), dostaw: 0, bezIdSzkoly: 0 };
      wpis.dostaw += 1;
      if (wpis.nazwa === null) wpis.nazwa = d.restaurant_name;
      const sid = joinKey(d.school_tuszama_id);
      if (sid === null) wpis.bezIdSzkoly += 1;
      else if (!wpis.szkoly.has(sid)) wpis.szkoly.set(sid, d.school_name);
      wpisy.set(rid, wpis);
    }
    const lista = [...wpisy.entries()]
      .map(([id, w]) => ({ id, ...w }))
      .sort((a, b) => b.szkoly.size - a.szkoly.size || (a.nazwa ?? '').localeCompare(b.nazwa ?? '', 'pl'));
    return {
      lista,
      dostawBezIdRestauracji,
      dostawWZbiorze: historia.data?.history.length ?? 0,
      szkolUnikalnych: new Set(lista.flatMap((w) => [...w.szkoly.keys()])).size,
    };
  }, [historia.data]);

  /*
   * ══════════════════════════════════════════════════════════════════════════════════════════
   * PORZĄDEK RANKINGU — sterowanie „Porządkuj wg" z makiety
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *
   * DO TEJ PORY TABELA NIE MIAŁA ŻADNEGO PORZĄDKU WŁASNEGO — renderowała wiersze w kolejności,
   * w jakiej przyszły z API. To nie jest neutralne: backend szereguje po własnym priorytecie
   * operacyjnym (kanon Szybkiego Alertu, spadki najpierw), czyli odpowiada na INNE pytanie niż
   * „która szkoła waży najwięcej". Ekran nazywa więc kolejność jawnie, tak jak Sprzedaż wg szkół.
   *
   * MAKIETA MA CZTERY WYMIARY, KONTRAKT NIESIE DWA I POŁOWĘ:
   *   Wartość       -> period_sales_grosze                      ODTWORZONE
   *   Porcje        -> tego źródło NIE ZWRACA. Podstawienie liczby zamówień byłoby zmyśleniem
   *                    wyniku — zamówienie i porcja to dwie różne jednostki (tak samo jak na
   *                    ekranie Sprzedaży wg szkół).                       BRAK KONTRAKTU
   *   Penetracja    -> wymaga liczby uczniów w szkole; nie ma jej w żadnej z tych odpowiedzi.
   *                                                                       BRAK KONTRAKTU
   *   Punktualność  -> jest, ale WYŁĄCZNIE gdy system kierowców jest podłączony I szkoła ma
   *                    dokładnie jeden rekord po tamtej stronie.          ODTWORZONE WARUNKOWO
   *
   * Zamiast „Porcji" oferujemy ZAMÓWIENIA — jednostkę, którą to źródło naprawdę liczy.
   */
  const punktualnoscSzkoly = (r: WierszSzkoly): number | null => {
    const k = joinKey(r.school_id);
    const grupa = k === null ? undefined : dostawyWgSzkoly.get(k);
    // Identyfikator wskazujący dwa rekordy Drive nie daje JEDNEJ punktualności — a wybranie
    // jednego z dwóch byłoby liczbą, której nie da się obronić. To jest „nie wiadomo".
    if (!grupa || grupa.length !== 1) return null;
    return grupa[0]!.current.on_time_pct;
  };

  const PORZADKI_SZKOL = {
    wartosc: {
      etykieta: 'Wartość',
      opis: 'malejąco po wartości katalogowej',
      klucz: (r: WierszSzkoly) => r.period_sales_grosze,
      kierunek: 'malejaco' as const,
    },
    zamowienia: {
      etykieta: 'Zamówienia',
      opis: 'malejąco po liczbie zamówień',
      klucz: (r: WierszSzkoly) => r.period_orders,
      kierunek: 'malejaco' as const,
    },
    ...(driveConnected
      ? {
          punktualnosc: {
            etykieta: 'Punktualność',
            opis: 'rosnąco — najsłabsza punktualność pierwsza, bo to lista do obdzwonienia',
            klucz: punktualnoscSzkoly,
            kierunek: 'rosnaco' as const,
          },
        }
      : {}),
  };

  type KluczPorzadku = keyof typeof PORZADKI_SZKOL;
  const [porzadekSzkol, setPorzadekSzkol] = useState<KluczPorzadku>('wartosc');
  /* Po odłączeniu Drive wybrany porządek mógłby wskazywać opcję, której już nie ma. */
  const wybranyPorzadek = PORZADKI_SZKOL[porzadekSzkol] ?? PORZADKI_SZKOL.wartosc;

  const wierszeUszeregowane = useMemo(
    () =>
      [...wiersze].sort(
        porzadek<WierszSzkoly>(wybranyPorzadek.klucz, wybranyPorzadek.kierunek, (a, b) =>
          (a.school_name ?? '').localeCompare(b.school_name ?? '', 'pl'),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wiersze, porzadekSzkol, driveConnected, dostawyWgSzkoly],
  );

  const szkolBezWartosciPorzadku = bezWartosci(wiersze, wybranyPorzadek.klucz);

  /*
   * GRAFIK: `restaurant_count` jest liczbą RÓŻNYCH restauracji z aktywnym wierszem grafiku.
   * Świadomie NIE sumujemy tej kolumny: restauracja dowożąca do dziesięciu szkół wpadłaby
   * do sumy dziesięć razy, a wynik nazwany „liczbą restauracji" byłby czystym zawyżeniem.
   */
  const zeZnanymGrafikiem = wiersze.filter((r) => typeof r.restaurant_count === 'number');
  const jednaRestauracja = zeZnanymGrafikiem.filter((r) => r.restaurant_count === 1).length;
  const bezWierszaGrafiku = zeZnanymGrafikiem.filter((r) => r.restaurant_count === 0).length;

  const zamowienia = sales.data?.summary.total_orders ?? null;
  const ziarno = { wierszy: zamowienia, bytow: sales.data ? wiersze.length : null };
  const zrodloPuste = czyPustyZbior(ziarno);
  const wartoscSprzedazy = wartoscDoPokazania(sales.data?.summary.total_sales_grosze, ziarno);
  const szkolDoPokazania = wartoscDoPokazania(sales.data ? szkolyUnikalne : null, ziarno);
  const szkolZOdstepstwem = outliersBySchool.size;

  const podstawa = { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' } as const;

  const dowodSzkol: Dowod = {
    tytul: 'Szkoły ze sprzedażą w oknie',
    etykietaWartosci: 'Liczba RÓŻNYCH szkół',
    wartosc: szkolDoPokazania === null ? null : formatCount(szkolDoPokazania),
    podstawa: { volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales → crm_order_items ⋈ crm_transactions, szkoła z TRANSAKCJI',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    oknoPorownawcze: dishes.data?.range.comparison
      ? oknoDoslownie(dishes.data.range.comparison.date_from, dishes.data.range.comparison.date_to)
      : null,
    wyliczenie: 'rozmiar zbioru RÓŻNYCH identyfikatorów szkół w wyniku — nie liczba zwróconych wierszy',
    szczegoly: [
      ['Wiersze w odpowiedzi', formatCount(sales.data ? wiersze.length : null)],
      ['Różne identyfikatory szkół', formatCount(sales.data ? szkolyUnikalne : null)],
      ['Wiersze bez identyfikatora', formatCount(sales.data ? wierszyBezIdentyfikatora : null)],
      ['Wiersze powtórzone', formatCount(sales.data ? wierszyPowtorzonych : null)],
      ['Przypisanie do szkoły', 'szkoła z TRANSAKCJI (stan z dnia zakupu)'],
      ['Zamówienia w oknie', formatCount(zamowienia)],
    ],
    proba: `${formatCount(zamowienia)} zamówień w ${formatCount(sales.data ? szkolyUnikalne : null)} szkołach`,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero wierszy i zero zamówień w tym oknie. ' +
              'To NIE jest zmierzone zero — nie ma czego liczyć.',
          ]
        : []),
      ...(wierszyPowtorzonych > 0
        ? [
            `Źródło zwróciło ${wierszyPowtorzonych} wiersze więcej niż różnych szkół. ` +
              'Liczba szkół pochodzi ze zbioru identyfikatorów, więc nie jest zawyżona — ale ' +
              'sam rozjazd jest sygnałem, że po stronie źródła powiela się para szkoła–restauracja.',
          ]
        : []),
      ...(wierszyBezIdentyfikatora > 0
        ? [`${wierszyBezIdentyfikatora} wierszy nie ma identyfikatora szkoły i nie wchodzi do tej liczby.`]
        : []),
      'Lista szkół pochodzi z aktywności operacyjnej w bieżącym sezonie, a nie z grafiku dostaw.',
    ],
    czegoNieWynika:
      'Z tej liczby NIE wynika, do ilu szkół TuSzama dowozi: to szkoły, które w tym oknie mają ' +
      'sprzedaż w CRM, a nie szkoły z grafiku dostaw — szkoła z aktywnym grafikiem i zerem zamówień ' +
      'w oknie tu nie występuje. Nie wynika też, ile restauracji je obsługuje: kolumny „Restauracje ' +
      'w grafiku" nie wolno zsumować, bo restauracja dowożąca do wielu szkół wpadłaby do sumy wiele razy.',
  };

  const dowodGrafiku: Dowod = {
    tytul: 'Restauracje w grafiku dostaw (per szkoła)',
    etykietaWartosci: 'Szkoły z dokładnie jedną restauracją',
    wartosc: zeZnanymGrafikiem.length > 0 ? formatCount(jednaRestauracja) : null,
    zrodlo: 'CRM /analytics/school-sales → crm_delivery_schedule (is_active = TRUE), COUNT DISTINCT restauracji',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — grafik jest snapshotem systemu głównego, bez stempla odczytu w odpowiedzi',
    oknoBiezace: 'BIEŻĄCY STAN GRAFIKU — nie okno analizy',
    oknoPorownawcze: null,
    wyliczenie: 'liczba RÓŻNYCH restauracji z aktywnym wierszem grafiku dla danej szkoły',
    szczegoly: [
      ['Ziarno grafiku', 'wiersz = (szkoła, restauracja, dzień tygodnia) z własnym cutoffem'],
      ['Dlaczego DISTINCT', 'ta sama para powtarza się dla każdego dnia dowozu i każdej godziny zamknięcia'],
      ['Szkoły ze znanym grafikiem', formatCount(sales.data ? zeZnanymGrafikiem.length : null)],
      ['W tym z jedną restauracją', formatCount(sales.data ? jednaRestauracja : null)],
      ['W tym bez aktywnego wiersza', formatCount(sales.data ? bezWierszaGrafiku : null)],
    ],
    proba: `${formatCount(sales.data ? zeZnanymGrafikiem.length : null)} szkół ze znanym grafikiem`,
    ograniczenia: [
      'Kolumna opisuje stan grafiku NA TERAZ, a nie stan w oknie analizy — te dwie rzeczy mogą się różnić.',
      'Grafik bywa wyłączony poza sezonem, więc zero nie jest tu rzadkością i nie jest awarią.',
    ],
    czegoNieWynika:
      'Zero w tej kolumnie NIE znaczy, że szkoła nie ma dostawcy — znaczy, że nie ma dziś aktywnego ' +
      'wiersza grafiku, co poza sezonem jest stanem normalnym; sprzedaż w oknie może istnieć mimo zera. ' +
      'Jedynka NIE znaczy też, że dostawca jest tylko jeden każdego dnia: liczba obejmuje cały tydzień, ' +
      'a poszczególne dni mogą mieć różne restauracje i różne godziny zamknięcia zamówień.',
  };

  const dowodZasiegu: Dowod = {
    tytul: 'Szkoły obsługiwane przez restaurację (z dostaw)',
    etykietaWartosci: 'Różne szkoły w oknie',
    wartosc: driveConnected && historia.data ? formatCount(zasieg.szkolUnikalnych) : null,
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'Drive /analytics/delivery-history → surowe rekordy dostaw (para restauracja–szkoła)',
    klasyfikacja: klasyfikacjaDrive(),
    swiezosc: swiezoscDrive(),
    oknoBiezace: okno,
    oknoPorownawcze: null,
    wyliczenie:
      'grupowanie rekordów po identyfikatorze restauracji; liczba szkół = rozmiar zbioru RÓŻNYCH ' +
      'identyfikatorów szkół w tej grupie',
    szczegoly: [
      ['Ziarno źródła', 'jeden wiersz na jedną DOSTAWĘ — para powtarza się każdego dnia dowozu'],
      ['Rekordy w zbiorze', formatCount(historia.data ? zasieg.dostawWZbiorze : null)],
      ['Rekordy dopasowane do restauracji', formatCount(historia.data ? historia.data.history.length - zasieg.dostawBezIdRestauracji : null)],
      ['Rekordy bez identyfikatora restauracji', formatCount(historia.data ? zasieg.dostawBezIdRestauracji : null)],
      ['Restauracje w zbiorze', formatCount(historia.data ? zasieg.lista.length : null)],
    ],
    proba: `${formatCount(historia.data ? zasieg.dostawWZbiorze : null)} rekordów dostaw`,
    ograniczenia: [
      ...(historia.data?.truncated
        ? [
            `Lista rekordów została UCIĘTA do ${formatCount(historia.data.limit ?? null)} z ` +
              `${formatCount(historia.data.total ?? null)} pasujących. Liczba szkół jest wtedy DOLNĄ ` +
              'granicą — restauracja mogła dowozić także tam, skąd żaden rekord się nie zmieścił.',
          ]
        : []),
      'Zbiór obejmuje wyłącznie dostawy zapisane w systemie kierowców w tym oknie.',
    ],
    czegoNieWynika:
      'Nie wynika z tego, że restauracja obsługuje TYLKO te szkoły ani że obsługuje je nadal: to lista ' +
      'szkół, do których w tym oknie coś pojechało, a nie umowa ani grafik. Szkoła bez dostawy w oknie ' +
      '(ferie, brak zamówień, dostawa spoza systemu kierowców) w tym zbiorze nie istnieje.',
  };

  const dowodDostaw: Dowod = {
    tytul: 'Szkoły z danymi o dostawach',
    etykietaWartosci: 'Różne identyfikatory szkół po stronie Drive',
    wartosc: driveConnected && delivery.data ? formatCount(szkolyZDostawami) : null,
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'Drive /analytics/delivery-by-school → utrwalony status dostawy',
    klasyfikacja: klasyfikacjaDrive(),
    swiezosc: swiezoscDrive(),
    oknoBiezace: okno,
    oknoPorownawcze: null,
    wyliczenie: 'rozmiar zbioru różnych `tuszama_id` w odpowiedzi — klucz złączenia ze sprzedażą',
    szczegoly: [
      ['Wiersze w odpowiedzi', formatCount(delivery.data?.schools.length ?? null)],
      ['Różne identyfikatory', formatCount(delivery.data ? szkolyZDostawami : null)],
      ['Identyfikatory z więcej niż jednym rekordem', formatCount(delivery.data ? identyfikatoryNiejednoznaczne : null)],
    ],
    proba: `${formatCount(delivery.data?.schools.length ?? null)} wierszy dostaw`,
    ograniczenia: [
      ...(identyfikatoryNiejednoznaczne > 0
        ? [
            `${identyfikatoryNiejednoznaczne} identyfikatorów wskazuje więcej niż jeden rekord szkoły ` +
              'po stronie Drive. Dla tych szkół punktualności NIE pokazujemy — wybranie jednego rekordu ' +
              'z dwóch byłoby liczbą, której nie da się obronić.',
          ]
        : []),
      'Rekordy dostaw bez identyfikatora systemu głównego w ogóle nie wchodzą do złączenia.',
    ],
    czegoNieWynika:
      'Nie wynika z tego, że pozostałe szkoły nie mają dostaw — mogą mieć dostawy bez identyfikatora ' +
      'systemu głównego albo w ogóle nie być prowadzone w systemie kierowców. To jest miara POKRYCIA ' +
      'danymi, nie miara działalności.',
  };

  const ladowanie = sales.isLoading || dishes.isLoading;

  return (
    <Box>
      <NaglowekEkranu
        tytul="Szkoły"
        opis="Do których szkół trafia oferta restauracji, ile tam sprzedaje, jak tam dowozimy — i gdzie konkretne danie wypada nietypowo słabo na tle pozostałych szkół tej samej restauracji."
        akcja={<DateRangeFilter wariant="naglowek" comparison={dishes.data?.range.comparison} />}
      />

      {ladowanie ? <LoadingState /> : null}
      {sales.error ? (
        <ErrorState
          error={sales.error}
          context="Control Center czyta wyłącznie API. Bez odpowiedzi backendu nie pokazujemy liczb zastępczych."
        />
      ) : null}

      {!ladowanie && !sales.error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          <PlytaGlowna
            kartaId="SCHOOLS-01"
            etykieta="Szkoły ze sprzedażą w oknie"
            wartosc={szkolDoPokazania === null ? null : formatCount(szkolDoPokazania)}
            opis={
              <Typography variant="body2" data-cc-card-id="SCHOOLS-02">
                {zrodloPuste
                  ? 'źródło zwróciło pusty zbiór w tym oknie — to nie jest zmierzone zero, tylko brak danych do policzenia'
                  : `liczba RÓŻNYCH szkół, nie wierszy · wartość katalogowa ${
                      wartoscSprzedazy === null ? 'brak danych' : formatGrosze(wartoscSprzedazy)
                    } (to nie jest kwota pobrana)`}
              </Typography>
            }
            zrodlo="CRM /analytics/school-sales → szkoła z transakcji (stan z dnia zakupu)"
            okno={okno}
            dowod={dowodSzkol}
            dowodId="SCHOOLS-21"
            boczne={
              <>
                <MiniMetryka kartaId="SCHOOLS-03" wartosc={formatCount(zamowienia)} etykieta="zamówień w oknie" />
                <MiniMetryka
                  kartaId="SCHOOLS-04"
                  wartosc={zeZnanymGrafikiem.length > 0 ? formatCount(jednaRestauracja) : null}
                  etykieta="z jedną restauracją w grafiku"
                />
                <MiniMetryka
                  kartaId="SCHOOLS-05"
                  wartosc={driveConnected && delivery.data ? formatCount(szkolyZDostawami) : null}
                  etykieta="z danymi o dostawach"
                />
                <MiniMetryka
                  kartaId="SCHOOLS-06"
                  wartosc={dishes.data ? formatCount(szkolZOdstepstwem) : null}
                  etykieta="z odstępstwem dania"
                />
              </>
            }
          />

          <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <MetricBasisChips basis={podstawa} />
            <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
              okno {okno}
            </Typography>
            <PrzyciskDowodu dowod={dowodGrafiku} etykieta="Dowód grafiku" kartaId="SCHOOLS-22" />
            {driveConnected ? (
              <PrzyciskDowodu dowod={dowodDostaw} etykieta="Dowód dostaw" kartaId="SCHOOLS-23" />
            ) : null}
          </Stack>

          {zrodloPuste ? (
            <Alert severity="warning">
              Źródło sprzedaży zwróciło pusty zbiór: zero wierszy i zero zamówień w oknie {okno}. To jest
              stan integracji, a nie wynik sprzedaży.
            </Alert>
          ) : null}

          {wierszyPowtorzonych > 0 ? (
            <Alert severity="warning" data-cc-card-id="SCHOOLS-07">
              Źródło zwróciło {formatCount(wiersze.length)} wierszy przy {formatCount(szkolyUnikalne)} różnych
              szkołach. Liczba szkół powyżej pochodzi ze zbioru identyfikatorów, więc nie jest zawyżona —
              ale powtórzone wiersze oznaczają, że para szkoła–restauracja powiela się po stronie źródła.
            </Alert>
          ) : null}

          {identyfikatoryNiejednoznaczne > 0 ? (
            <Alert severity="warning" data-cc-card-id="SCHOOLS-08">
              {formatCount(identyfikatoryNiejednoznaczne)} identyfikatorów szkoły wskazuje więcej niż jeden
              rekord po stronie systemu kierowców. Dla tych szkół punktualności nie pokazujemy — wybranie
              jednego rekordu z dwóch byłoby liczbą, której nie da się obronić.
            </Alert>
          ) : null}

          {!driveConnected ? (
            <Alert severity="info">
              Wymaga podłączenia systemu kierowców: kolumny punktualności i dostaw oraz zasięg dowozu są bez
              niego niedostępne. Nie pokazujemy ich pustych, bo pusta kolumna wygląda jak brak problemu.
            </Alert>
          ) : null}

          {sales.data && wiersze.length === 0 ? (
            <EmptyState
              title="Brak sprzedaży w wybranym oknie"
              description={`Okno ${okno}. Źródło nie zwróciło ani jednej szkoły — nie ma czego zestawiać z dostawami.`}
            />
          ) : null}

          {sales.data && wiersze.length > 0 ? (
            <>
              <PasSekcji
                tytul="Sprzedaż i dostawy"
                opis="Jedna szkoła w wierszu: ile kupiła i jak jej dowożono w tym samym oknie."
                akcja={
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                      Porządkuj wg
                    </Typography>
                    <Segmenty
                      etykieta="Porządek rankingu szkół"
                      wybrana={porzadekSzkol}
                      onZmiana={(w) => setPorzadekSzkol(w)}
                      opcje={Object.entries(PORZADKI_SZKOL).map(([k, v]) => ({
                        wartosc: k as KluczPorzadku,
                        etykieta: v.etykieta,
                      }))}
                    />
                  </Stack>
                }
              />

              <Karta nadtytul="Jedna szkoła = jeden wiersz" tytul="Sprzedaż i dostawy">
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, maxWidth: '86ch' }}>
                Kolejność: {wybranyPorzadek.opis}. Nadana na tym ekranie, nie przejęta z API —
                backend szereguje wiersze wg własnego priorytetu operacyjnego, co jest odpowiedzią
                na inne pytanie.
                {szkolBezWartosciPorzadku > 0 ? (
                  <>
                    {' '}
                    <strong>{formatCount(szkolBezWartosciPorzadku)}</strong>{' '}
                    {szkolBezWartosciPorzadku === 1 ? 'szkoła nie ma' : 'szkół nie ma'} tej wielkości
                    zmierzonej i {szkolBezWartosciPorzadku === 1 ? 'stoi' : 'stoją'} na końcu listy —
                    z braku pomiaru, nie z wyniku.
                  </>
                ) : null}{' '}
                Makieta oferuje jeszcze porcje i penetrację; pierwszej to źródło nie zwraca (zamówienie
                to nie porcja), druga wymaga liczby uczniów, której nie ma w żadnej z tych odpowiedzi.
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label={`Szkoły — sprzedaż i dostawy, ${wybranyPorzadek.opis}`}>
                  <TableHead>
                    <TableRow>
                      {/* Adresy kolumn (R5-15): identyfikator siedzi na NAGŁÓWKU kolumny, bo nagłówek
                          jest w tabeli jedyny, a komórka wartości powtarza się raz na każdą szkołę.
                          Atrybut nie zmienia wyglądu — jest wyłącznie adresem kolumny. */}
                      <TableCell data-cc-card-id="SCHOOLS-09">Szkoła</TableCell>
                      <TableCell>Miasto</TableCell>
                      <TableCell align="right" data-cc-card-id="SCHOOLS-10">Wartość katalogowa</TableCell>
                      <TableCell align="right" data-cc-card-id="SCHOOLS-11">Zamówienia</TableCell>
                      <TableCell align="right" data-cc-card-id="SCHOOLS-12">Restauracje w grafiku</TableCell>
                      {driveConnected ? <TableCell align="right" data-cc-card-id="SCHOOLS-13">Na czas</TableCell> : null}
                      {driveConnected ? <TableCell align="right" data-cc-card-id="SCHOOLS-14">Dostawy</TableCell> : null}
                      <TableCell data-cc-card-id="SCHOOLS-15">Odstępstwa dań</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wierszeUszeregowane.map((row) => {
                      const key = joinKey(row.school_id);
                      const grupa = key === null ? undefined : dostawyWgSzkoly.get(key);
                      const niejednoznaczna = (grupa?.length ?? 0) > 1;
                      const d = grupa && grupa.length === 1 ? grupa[0] : undefined;
                      const outliers = key === null ? [] : (outliersBySchool.get(key) ?? []);
                      return (
                        <TableRow key={String(row.school_id)} hover>
                          <TableCell>{row.school_name ?? `#${row.school_id}`}</TableCell>
                          <TableCell>{row.city ?? '—'}</TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatGrosze(row.period_sales_grosze)}
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatCount(row.period_orders)}
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {/* Brak pola (starszy backend) to „brak danych", a nie zero restauracji. */}
                            {typeof row.restaurant_count === 'number' ? (
                              <Box
                                component="span"
                                title="Liczba RÓŻNYCH restauracji z aktywnym wierszem grafiku dostaw. Stan bieżący, nie stan z okna."
                                sx={{ color: row.restaurant_count === 1 ? 'var(--uwaga)' : undefined }}
                              >
                                {formatCount(row.restaurant_count)}
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                                brak danych
                              </Typography>
                            )}
                          </TableCell>
                          {driveConnected ? (
                            <TableCell align="right" className="sygnal-liczba">
                              {niejednoznaczna ? (
                                <Box
                                  component="span"
                                  title={`Ten identyfikator szkoły wskazuje ${grupa?.length} rekordy po stronie systemu kierowców — nie da się wskazać jednej punktualności.`}
                                >
                                  niejednoznaczne
                                </Box>
                              ) : d === undefined || d.current.on_time_pct === null ? (
                                '—'
                              ) : (
                                `${d.current.on_time_pct}%`
                              )}
                            </TableCell>
                          ) : null}
                          {driveConnected ? (
                            <TableCell align="right" className="sygnal-liczba">
                              {d ? formatCount(d.current.deliveries) : '—'}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            {outliers.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            ) : (
                              outliers.map((o) => (
                                <Chip
                                  key={`${String(row.school_id)}-${o.meal_name ?? 'danie'}-${o.share_ratio}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ mr: 0.5 }}
                                  label={`${o.meal_name ?? 'danie'} ×${o.share_ratio}`}
                                  title="Udział tego dania w sprzedaży szkoły jest wyraźnie niższy niż mediana pozostałych szkół tej samej restauracji."
                                />
                              ))
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, maxWidth: '90ch' }}>
                „Odstępstwo” porównuje UDZIAŁ dania w sprzedaży danej szkoły z medianą pozostałych szkół tej
                samej restauracji. Szkoły nie są grupą kontrolną — różnią się liczbą uczniów, zamożnością,
                godziną przerwy i punktualnością dostaw. Odstępstwo jest współwystąpieniem, nie przyczyną.
              </Typography>
              </Karta>
            </>
          ) : null}

          {/* ZASIĘG DOWOZU — nagłówek świadomie bez słowa „szkoły": na tym ekranie tylko tytuł
              ekranu ma prawo je nieść, żeby nazwa ekranu pozostała jednoznaczna. */}
          <PasSekcji tytul="Zasięg dowozu" opis="Która restauracja dojeżdża gdzie — i ile razy." />

          <Karta
            nadtytul="Para restauracja–szkoła"
            tytul="Zasięg dowozu — restauracja po restauracji"
            akcja={
              driveConnected ? (
                <PrzyciskDowodu dowod={dowodZasiegu} etykieta="Skąd ta liczba" kartaId="SCHOOLS-24" />
              ) : null
            }
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '82ch' }}>
              Źródło ma jeden wiersz na jedną dostawę, więc ta sama para restauracja–szkoła powtarza się w
              nim tyle razy, ile razy coś przyjechało. Kolumna „szkoły” liczy RÓŻNE szkoły; kolumna
              „rekordy” stoi obok, żeby było widać, o ile te dwie liczby się różnią.
            </Typography>

            {!driveConnected ? (
              <EmptyState
                title="Zasięg dowozu wymaga systemu kierowców"
                description={`Okno ${okno}. Para restauracja–szkoła występuje wprost wyłącznie w rekordach dostaw, a te wymagają osobnego logowania.`}
              />
            ) : historia.isLoading ? (
              <LoadingState label="Wczytywanie rekordów dostaw…" />
            ) : historia.error ? (
              <ErrorState error={historia.error} />
            ) : zasieg.lista.length === 0 ? (
              <EmptyState
                title="Brak rekordów dostaw w wybranym oknie"
                description={`Okno ${okno}. Bez rekordów dostaw nie da się powiedzieć, która restauracja dowoziła gdzie — a zgadywać nie będziemy.`}
              />
            ) : (
              <>
                {historia.data?.truncated ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }} data-cc-card-id="SCHOOLS-19">
                    Zbiór rekordów został ucięty do {formatCount(historia.data.limit ?? null)} z{' '}
                    {formatCount(historia.data.total ?? null)} pasujących. Liczby szkół poniżej są DOLNĄ
                    granicą: restauracja mogła dowozić także tam, skąd żaden rekord się nie zmieścił. Zawęź okno.
                  </Alert>
                ) : null}

                {zasieg.dostawBezIdRestauracji > 0 ? (
                  <Alert severity="info" sx={{ mb: 1.5 }} data-cc-card-id="SCHOOLS-20">
                    {formatCount(zasieg.dostawBezIdRestauracji)} rekordów nie ma identyfikatora restauracji z
                    systemu głównego i nie wchodzi do tego zestawienia — zamiast dopisywać je „na oko”.
                  </Alert>
                ) : null}

                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Zasięg dowozu restauracji">
                    <TableHead>
                      <TableRow>
                        <TableCell>Restauracja</TableCell>
                        <TableCell align="right" data-cc-card-id="SCHOOLS-16">Różnych szkół</TableCell>
                        <TableCell align="right" data-cc-card-id="SCHOOLS-17">Rekordów dostaw</TableCell>
                        <TableCell data-cc-card-id="SCHOOLS-18">Gdzie dowoziła w oknie</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {zasieg.lista.map((w) => (
                        <TableRow key={w.id} hover>
                          <TableCell>{w.nazwa ?? `#${w.id}`}</TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatCount(w.szkoly.size)}
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba" sx={{ color: 'var(--tekst-slaby)' }}>
                            {formatCount(w.dostaw)}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                              {[...w.szkoly.entries()].map(([id, nazwa]) => (
                                <Chip key={id} size="small" variant="outlined" label={nazwa ?? `#${id}`} />
                              ))}
                              {w.bezIdSzkoly > 0 ? (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label={`${formatCount(w.bezIdSzkoly)} rekordów bez identyfikatora`}
                                  title="Rekordy dostaw bez identyfikatora szkoły z systemu głównego — nie da się ich uczciwie przypisać do konkretnej szkoły."
                                />
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Karta>
        </Stack>
      ) : null}
    </Box>
  );
}
